import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { availableIcons } from "@/utils/iconMapper";

interface ResourceManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  existingResource?: {
    id: string;
    title: string;
    description: string;
    filename: string;
    category: string;
    resource_type: string;
    icon_name: string;
  } | null;
}

export const ResourceManagementDialog = ({
  open,
  onOpenChange,
  onSuccess,
  existingResource,
}: ResourceManagementDialogProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [title, setTitle] = useState(existingResource?.title || "");
  const [description, setDescription] = useState(existingResource?.description || "");
  const [category, setCategory] = useState(existingResource?.category || "Planning");
  const [resourceType, setResourceType] = useState(existingResource?.resource_type || "PDF Guide");
  const [iconName, setIconName] = useState(existingResource?.icon_name || "FileText");
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (existingResource) {
      // Edit mode - populate with existing data
      setTitle(existingResource.title);
      setDescription(existingResource.description);
      setCategory(existingResource.category);
      setResourceType(existingResource.resource_type);
      setIconName(existingResource.icon_name);
      setFile(null);
    } else {
      // Add mode - reset to defaults
      setTitle("");
      setDescription("");
      setCategory("Planning");
      setResourceType("PDF Guide");
      setIconName("FileText");
      setFile(null);
    }
  }, [existingResource]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate file for new resources
      if (!existingResource && !file) {
        toast({
          title: "File required",
          description: "Please select a file to upload.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Validate file size (10MB max)
      if (file && file.size > 10 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Maximum file size is 10MB.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      
      if (file && !allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Only PDF, DOC, DOCX, and TXT files are allowed.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      let filename = existingResource?.filename || "";

      // Upload file if provided
      if (file) {
        // Sanitize filename
        const sanitizedFilename = file.name
          .replace(/\.\.\//g, '')
          .replace(/[^a-zA-Z0-9._-]/g, '_')
          .substring(0, 255);

        // Add timestamp to prevent overwrites
        const timestamp = Date.now();
        filename = `${timestamp}_${sanitizedFilename}`;

        const { error: uploadError } = await supabase.storage
          .from('resources')
          .upload(filename, file, {
            upsert: false,
          });

        if (uploadError) throw uploadError;
      }

      if (existingResource) {
        // Update existing resource
        const { error: updateError } = await supabase
          .from('resources')
          .update({
            title,
            description,
            category,
            resource_type: resourceType,
            icon_name: iconName,
            ...(file && { filename }), // Only update filename if new file uploaded
          })
          .eq('id', existingResource.id);

        if (updateError) throw updateError;

        // Delete old file if new one was uploaded
        if (file && existingResource.filename !== filename) {
          await supabase.storage
            .from('resources')
            .remove([existingResource.filename]);
        }
      } else {
        // Get the authenticated user for audit trail
        const { data: { user } } = await supabase.auth.getUser();

        // Insert new resource with created_by tracking
        const { error: insertError } = await supabase
          .from('resources')
          .insert({
            title,
            description,
            filename,
            category,
            resource_type: resourceType,
            icon_name: iconName,
            created_by: user?.id,
          });

        if (insertError) throw insertError;
      }

      toast({
        title: "Success!",
        description: existingResource ? "Resource updated successfully" : "Resource added successfully",
      });

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error managing resource:', error);
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{existingResource ? "Edit Resource" : "Add New Resource"}</DialogTitle>
          <DialogDescription>
            {existingResource 
              ? "Update the resource details and optionally upload a new file."
              : "Fill in the details and upload a file to create a new resource."
            }
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., SME Security Checklist"
              required
              maxLength={200}
            />
          </div>

          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of the resource..."
              required
              maxLength={500}
              rows={3}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="category">Category *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Planning">Planning</SelectItem>
                  <SelectItem value="Compliance">Compliance</SelectItem>
                  <SelectItem value="Tools">Tools</SelectItem>
                  <SelectItem value="Education">Education</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="resourceType">Resource Type *</Label>
              <Input
                id="resourceType"
                value={resourceType}
                onChange={(e) => setResourceType(e.target.value)}
                placeholder="e.g., PDF Guide, PDF Template"
                required
                maxLength={50}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="icon">Icon *</Label>
            <Select value={iconName} onValueChange={setIconName}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableIcons.map((icon) => (
                  <SelectItem key={icon} value={icon}>
                    {icon}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="file">
              File {existingResource ? "(optional - leave empty to keep current)" : "*"}
            </Label>
            <Input
              id="file"
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required={!existingResource}
            />
            {existingResource && (
              <p className="text-xs text-muted-foreground mt-1">
                Current file: {existingResource.filename}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {existingResource ? "Updating..." : "Adding..."}
                </>
              ) : (
                existingResource ? "Update Resource" : "Add Resource"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
